import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import {
  SignedIn,
  SignedOut,
  UserButton,
  SignIn,
  SignUp,
  useUser,
  RedirectToSignIn
} from '@clerk/clerk-react';
import Navbar from './components/Navbar';
import Budgets from './pages/Budgets';
import MoneyEducation from './pages/MoneyEducation';
import Accounts from './pages/Accounts';
import DebtManagement from './pages/DebtManagement';
import Savings from './pages/Savings';
import Investments from './pages/Investments';
import Dashboard from './pages/Dashboard';
import NetWorth from './pages/NetWorth';
import CreditCardRewards from './pages/CreditCardRewards';
import './App.css';
import 'tailwindcss/tailwind.css';

// Public Layout Component
const PublicLayout = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <header className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Left: Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img src="/logo.svg" alt="Thrynx Logo" className="h-8 w-auto" />
              <span className="ml-2 text-xl font-bold">Thrynx</span>
            </Link>
          </div>
          
          {/* Center: Navbar (only when signed in) */}
          <div className="flex-grow mx-4 flex justify-center">
            <SignedIn>
              <Navbar />
            </SignedIn>
          </div>
          
          {/* Right: User controls */}
          <div className="flex-shrink-0">
            <SignedIn>
              <UserButton />
            </SignedIn>
            <SignedOut>
              <div className="flex items-center space-x-4">
                <Link to="/sign-in" className="text-gray-700 hover:text-blue-600 px-3 py-2">
                  Sign in
                </Link>
                <Link
                  to="/sign-up"
                  className="ml-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Get started
                </Link>
              </div>
            </SignedOut>
          </div>
        </div>
      </div>
    </header>
    <main className="flex-grow">{children}</main>
    <footer className="bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-center text-gray-500 text-sm">
          {new Date().getFullYear()} Thrynx Inc. | team@thrynx.com
        </p>
      </div>
    </footer>
  </div>
);

// App Store Badge Component
const AppStoreBadge = ({ store, className = '' }) => {
  const stores = {
    apple: {
      name: 'App Store',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16.365 1.43c1.032 0 2.05.057 3.061.17a.5.5 0 01.45.5c.007.6.01 1.2 0 1.8a.5.5 0 01-.3.46 7.3 7.3 0 01-1.92.26c-1.03 0-2.07-.15-3.08-.44a.5.5 0 01-.33-.63c.07-.25.25-.42.5-.49.9-.25 1.84-.38 2.72-.63zM5.345 3.99c1.1-.15 2.2-.25 3.3-.35a.5.5 0 00.45-.5c0-.63.01-1.25 0-1.88a.5.5 0 00-.5-.5 22.4 22.4 0 00-3.28.25.5.5 0 00-.46.5c-.01.63-.01 1.25 0 1.88 0 .28.22.5.5.5.54.05 1.08.1 1.61.15z"/>
          <path d="M22.5 7.5v9c0 .28-.22.5-.5.5h-20a.5.5 0 01-.5-.5v-9a.5.5 0 01.5-.5h20c.28 0 .5.22.5.5z"/>
          <path d="M22.5 16.5v2c0 .28-.22.5-.5.5h-20a.5.5 0 01-.5-.5v-2a.5.5 0 01.5-.5h20c.28 0 .5.22.5.5z"/>
        </svg>
      ),
    },
    google: {
      name: 'Google Play',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3.61 1.71L14.54 12.6 12 15.14 1.56 4.7c-.15-.15-.15-.4 0-.56L3.6 1.7zM1.7 19.3l3.16-3.16 2.4 2.4c-.9.7-2.1 1.2-3.4 1.2-1.3 0-2.5-.5-3.4-1.3-.2-.15-.2-.4 0-.55zM7.26 18.74l9.9-9.9 3.2 3.2c.7.9 1.1 2 1.1 3.2 0 1.3-.5 2.5-1.3 3.4l-3.2-3.2-9.7 9.7z"/>
          <path d="M14.5 12l-2.1 2.1 2.1 2.1 2.1-2.1z"/>
        </svg>
      ),
    },
  };

  return (
    <div className={`relative group ${className}`}>
      <div className="flex items-center justify-center px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer">
        <div className="mr-2 text-gray-700">
          {stores[store].icon}
        </div>
        <div className="text-left">
          <div className="text-xs text-gray-500">Download on the</div>
          <div className="font-semibold text-gray-800">{stores[store].name}</div>
        </div>
      </div>
      <div className="absolute inset-0 bg-black bg-opacity-60 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="text-white font-medium text-sm">Coming Soon</span>
      </div>
    </div>
  );
};

// Home Page Component
const Home = () => (
  <div className="container mx-auto px-4 py-4 md:py-8">
    {/* Hero Section with Improved Value Proposition */}
    <section className="text-center max-w-4xl mx-auto mb-8 md:mb-12">
      <div className="mb-2">
        <span className="bg-blue-50 text-blue-700 text-sm font-medium px-3 py-0.5 rounded-full inline-block">
          Launching Winter 2026
        </span>
      </div>
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 leading-tight">
        The AI Agent for Your Money
      </h1>
      <p className="text-base md:text-lg text-gray-600 mb-4 max-w-2xl mx-auto">
        Thrynx helps you maximize credit card rewards, automate savings, and invest smarter.
        Turn your everyday spending into long-term wealth.
      </p>
      <div className="flex flex-col items-center space-y-4 mb-6">
        <div className="flex flex-wrap justify-center gap-4">
          <SignedOut>
            <Link
              to="/sign-up"
              className="bg-blue-600 text-white px-8 py-3 rounded-md text-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              Join Waitlist
            </Link>
            <Link
              to="/sign-in"
              className="bg-white text-gray-700 px-8 py-3 border border-gray-300 rounded-md text-lg font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              Sign In
            </Link>
          </SignedOut>
          <SignedIn>
            <Link
              to="/dashboard"
              className="bg-blue-600 text-white px-8 py-3 rounded-md text-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              Go to Dashboard
            </Link>
          </SignedIn>
        </div>
        
        {/* App Store Badges */}
        <div className="pt-2">
          <p className="text-xs text-gray-500 mb-2">MOBILE APP COMING SOON</p>
          <div className="flex flex-wrap justify-center gap-3">
            <AppStoreBadge store="apple" />
            <AppStoreBadge store="google" />
          </div>
        </div>
      </div>
      
      {/* Social Proof */}
      <div className="mt-12 pt-8 border-t border-gray-100">
        <p className="text-gray-500 text-sm mb-4">JOINED BY 1500+ EARLY USERS FROM</p>
        <div className="flex flex-wrap justify-center items-center gap-8 opacity-70">
          <span className="text-gray-700 font-medium">Harvard</span>
          <span className="text-gray-700 font-medium">Stanford</span>
          <span className="text-gray-700 font-medium">MIT</span>
        </div>
      </div>
    </section>

    {/* Features Section */}
    <section className="py-8 md:py-10 bg-gray-50 rounded-2xl my-8 md:my-10">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 md:mb-6">Your Personal Finance, Optimized</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: '💰',
              title: 'Budget Effortlessly',
              description: 'Automated tracking and smart categorization of your spending across all accounts.'
            },
            {
              icon: '📈',
              title: 'Maximize Rewards',
              description: 'AI-powered recommendations to ensure you\'re using the best card for every purchase.'
            },
            {
              icon: '🧠',
              title: 'Grow Your Wealth',
              description: 'Automated investing of rewards and savings to build long-term wealth.'
            }
          ].map((feature, index) => (
            <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">{feature.icon}</div>
              <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Video Section */}
    <section className="py-8 md:py-10 my-8 md:my-10">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-4 md:mb-6">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">The Thrynx Philosophy</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Discover how small, smart financial decisions compound into significant wealth over time.
          </p>
        </div>
        <div className="relative pt-[56.25%] rounded-xl overflow-hidden shadow-lg">
          <iframe 
            className="absolute top-0 left-0 w-full h-full"
            src="https://www.youtube.com/embed/3AUbMHtz-YY" 
            title="Thrynx - Your AI Financial Agent" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowFullScreen>
          </iframe>
        </div>
      </div>
    </section>

    {/* Final CTA - Only shown when signed out */}
    <SignedOut>
      <section className="text-center py-8 md:py-10 bg-blue-50 rounded-2xl my-8 md:my-10">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Ready to transform your finances?</h2>
          <p className="text-gray-600 mb-3">
            Join our waitlist today and be the first to experience the future of personal finance.
          </p>
          <Link
            to="/sign-up"
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-md text-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Join Waitlist - It's Free
          </Link>
          <p className="text-sm text-gray-500 mt-4">No credit card required • Cancel anytime</p>
        </div>
      </section>
    </SignedOut>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isLoaded, isSignedIn } = useUser();
  const location = useLocation();

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  return children;
};

// Main App Component
function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={
        <PublicLayout>
          <Home />
        </PublicLayout>
      } />
      
      {/* Auth routes */}
      <Route
        path="/sign-in/*"
        element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8
            ">
              <div className="text-center">
                <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Sign in to your account</h2>
              </div>
              <SignIn 
                routing="path" 
                path="/sign-in" 
                signUpUrl="/sign-up"
                fallbackRedirectUrl="/dashboard"
                forceRedirectUrl="/dashboard"
              />
            </div>
          </div>
        }
      />
      <Route
        path="/sign-up/*"
        element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
              <div className="text-center">
                <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Create your account</h2>
              </div>
              <SignUp 
                routing="path" 
                path="/sign-up" 
                signInUrl="/sign-in"
                fallbackRedirectUrl="/dashboard"
                forceRedirectUrl="/dashboard"
              />
            </div>
          </div>
        }
      />
      
      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <PublicLayout>
              <Dashboard />
            </PublicLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/budgets"
        element={
          <ProtectedRoute>
            <PublicLayout>
              <Budgets />
            </PublicLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/debt-management"
        element={
          <ProtectedRoute>
            <PublicLayout>
              <DebtManagement />
            </PublicLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/savings"
        element={
          <ProtectedRoute>
            <PublicLayout>
              <Savings />
            </PublicLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/investments"
        element={
          <ProtectedRoute>
            <PublicLayout>
              <Investments />
            </PublicLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/net-worth"
        element={
          <ProtectedRoute>
            <PublicLayout>
              <NetWorth />
            </PublicLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/money-education"
        element={
          <ProtectedRoute>
            <PublicLayout>
              <MoneyEducation />
            </PublicLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/accounts"
        element={
          <ProtectedRoute>
            <PublicLayout>
              <Accounts />
            </PublicLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/credit-card-rewards"
        element={
          <ProtectedRoute>
            <PublicLayout>
              <CreditCardRewards />
            </PublicLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Catch all other routes */}
      <Route
        path="*"
        element={
          <PublicLayout>
            <div className="min-h-[50vh] flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                <p className="text-xl text-gray-600 mb-6">Page not found</p>
                <Link
                  to="/"
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Go back home
                </Link>
              </div>
            </div>
          </PublicLayout>
        }
      />
    </Routes>
  );
}

export default App;
